/**
 * Event Detail Page - REDESIGNED
 * Layout: Title → Badges → Two-Column (Details | Flyer) → Going → Description → Gallery
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import GoingButton from '../components/GoingButton';
import Gallery from '../components/Gallery';
import Navbar from '../components/Navbar';
import { navigateBack, cameFrom } from '../utils/navigationHistory';
import { formatDateLong, formatTime } from '../utils/dateFormatter';
import '../styles/colors.css';

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [collaborators, setCollaborators] = useState({ bands: [], venues: [], promoters: [], vendors: [] });

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const response = await apiClient.get(`/events/${id}`);
      if (response.data.success) {
        setEvent(response.data.event);
        
        try {
          const collabResponse = await apiClient.get(`/events/${id}/collaborators`);
          if (collabResponse.data.success) {
            const collabs = collabResponse.data.collaborators;
            setCollaborators({
              bands: collabs.filter(c => c.collaborator_role === 'band'),
              venues: collabs.filter(c => c.collaborator_role === 'venue'),
              promoters: collabs.filter(c => c.collaborator_role === 'promoter'),
              vendors: collabs.filter(c => c.collaborator_role === 'vendor' || c.collaborator_role === 'photographer')
            });
          }
        } catch (err) {
          console.error('Failed to load collaborators:', err);
        }
      }
    } catch (err) {
      setError('Event not found');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/events/${id}`);
      if (response.data.success) {
        alert('Event deleted successfully');
        navigate('/news?tab=events');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Loading event...
        </div>
      </>
    );
  }

  if (error || !event) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
          <p style={{ color: 'var(--accent-danger)', fontSize: '18px' }}>{error || 'Event not found'}</p>
          <button onClick={() => navigate('/news?tab=events')} className="btn btn-secondary">Back to Events</button>
        </div>
      </>
    );
  }

  const isCreator = user && user.id === event.created_by;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .event-title {
            font-size: 32px !important;
          }

          .event-details-grid {
            display: flex !important;
            flex-direction: column !important;
          }

          .event-flyer-container {
            order: 1;
            margin-bottom: 0 !important;
          }

          .event-flyer-container img {
            max-height: 400px !important;
          }

          .going-button-mobile {
            order: 2;
            margin-top: 24px;
            margin-bottom: 24px;
          }

          .event-details-card {
            order: 3;
            margin-bottom: 24px;
          }

          .event-details-text {
            line-height: 1.4 !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>

          <h1 className="event-title" style={{ color: 'var(--text-light)', fontSize: '48px', fontWeight: '700', marginBottom: '20px', lineHeight: '1.2' }}>
            {event.title}
          </h1>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
            <span className="badge badge-primary" style={{ fontSize: '14px', padding: '8px 16px' }}>Event</span>
            <span style={{ background: 'var(--smoke-gray)', color: 'var(--text-light)', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '700' }}>
              {event.age_restriction === '21_plus' ? '21+' : event.age_restriction === '18_plus' ? '18+' : 'All Ages'}
            </span>
            <span style={{ background: 'transparent', border: '2px solid var(--accent-primary)', color: 'var(--accent-primary)', padding: '6px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '700' }}>
              {event.going_count || 0} Going
            </span>
          </div>

          <div className="event-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>
            
            {/* Flyer - will appear first on mobile */}
            <div className="event-flyer-container" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              {event.flyer_url ? (
                <img src={event.flyer_url} alt={event.title} 
                     style={{ maxWidth: '100%', maxHeight: '700px', borderRadius: '12px', boxShadow: 'var(--shadow-card)' }}
                     onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div style={{ width: '100%', aspectRatio: '2/3', background: 'var(--smoke-gray)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '18px' }}>
                  No Flyer
                </div>
              )}
            </div>

            {/* Details card */}
            <div className="event-details-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
              <div className="event-details-text" style={{ fontSize: '16px', lineHeight: '1.8', color: 'var(--text-dark)' }}>
                
                <p style={{ marginBottom: '12px' }}><strong>Date:</strong> {formatDateLong(event.event_date)}</p>
                
                <p style={{ marginBottom: '12px' }}>
                  <strong>Doors:</strong> {formatTime(event.door_time)} 
                  <span style={{ marginLeft: '20px' }}><strong>Music:</strong> {formatTime(event.music_time)}</span>
                </p>

                <p style={{ marginBottom: '12px' }}><strong>Entry:</strong> {event.ticket_price === 0 ? 'Free' : `$${event.ticket_price}`}</p>
                
                {collaborators.venues.length > 0 && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Venue:</strong>{' '}
                    {collaborators.venues.map((v, i) => (
                      <span key={i}>
                        {v.collaborator_id ? (
                          <a href={`/users/${v.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${v.collaborator_id}`); }} 
                             style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer', fontWeight: '600' }}>
                            {v.collaborator_name}
                          </a>
                        ) : (
                          <span style={{ fontWeight: '600' }}>{v.collaborator_name}</span>
                        )}
                        {i < collaborators.venues.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}

                {event.address && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Address:</strong> {event.address}
                    {event.city && event.state && (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {', '}{event.city}, {event.state}
                      </span>
                    )}
                  </p>
                )}
                {!event.address && event.city && event.state && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Location:</strong> {event.city}, {event.state}
                  </p>
                )}

                {collaborators.bands.length > 0 && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Bands/Artists:</strong>{' '}
                    {collaborators.bands.map((b, i) => (
                      <span key={i}>
                        {b.collaborator_id ? (
                          <a href={`/users/${b.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${b.collaborator_id}`); }} 
                             style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                            {b.collaborator_name}
                          </a>
                        ) : b.collaborator_name}
                        {i < collaborators.bands.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}

                {collaborators.promoters.length > 0 && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Promoters:</strong>{' '}
                    {collaborators.promoters.map((p, i) => (
                      <span key={i}>
                        {p.collaborator_id ? (
                          <a href={`/users/${p.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${p.collaborator_id}`); }} 
                             style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                            {p.collaborator_name}
                          </a>
                        ) : p.collaborator_name}
                        {i < collaborators.promoters.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}

                {collaborators.vendors.length > 0 && (
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Vendors:</strong>{' '}
                    {collaborators.vendors.map((v, i) => (
                      <span key={i}>
                        {v.collaborator_id ? (
                          <a href={`/users/${v.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${v.collaborator_id}`); }} 
                             style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                            {v.collaborator_name}
                          </a>
                        ) : v.collaborator_name}
                        {i < collaborators.vendors.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}

                {event.ticket_link && (
                  <p style={{ marginTop: '20px' }}>
                    <a href={event.ticket_link} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      Buy Tickets
                    </a>
                  </p>
                )}

                {event.genre_tags && event.genre_tags.length > 0 && (
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}>
                    <strong style={{ display: 'block', marginBottom: '12px' }}>Genre Tags:</strong>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {event.genre_tags.map((genre, i) => (
                        <span key={i} style={{ background: 'transparent', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Going Button - below grid on desktop, below flyer on mobile */}
          <div className="going-button-mobile" style={{ marginBottom: '32px' }}>
            <GoingButton eventId={id} onUpdate={fetchEvent} />
          </div>

          {event.description && (
            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700', color: 'var(--text-dark)' }}>Description</h2>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '16px', color: 'var(--text-dark)' }}>{event.description}</p>
            </div>
          )}

          {event.image_gallery_urls && event.image_gallery_urls.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-light)', fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Gallery</h2>
              <Gallery images={event.image_gallery_urls} />
            </div>
          )}

          {isCreator && (
            <div style={{ background: 'rgba(0, 217, 255, 0.1)', border: '2px solid var(--accent-primary)', borderRadius: '12px', padding: '24px', marginTop: '32px' }}>
              <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '16px' }}><strong>You created this event</strong></p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => navigate(`/events/${id}/edit`)} className="btn btn-primary">Edit Event</button>
                <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">{deleting ? 'Deleting...' : 'Delete Event'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventDetailPage;
